import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiFolderPlus, FiLayers, FiUsers, FiAlertTriangle } from 'react-icons/fi';
import {
  uploadKML,
  getProject,
  deleteProject as deleteProjectAPI,
  createProjectFolder,
  updateProjectFolder,
  deleteProjectFolder,
  deleteKmlLayer,
  updateKmlLayer,
  updateProjectName,
  addKmlToProject,
  getProjectAssignments,
  putProjectAssignments,
  getAssignmentEligibleUsers
} from '../services/api';
import './ManageProjectsModal.css';

const TABS = { create: 'create', layers: 'layers', members: 'members', danger: 'danger' };

function ManageProjectsModal({ isOpen, onClose, projects, onRefresh, onProjectSelect, user }) {
  const [activeTab, setActiveTab] = useState(TABS.create);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);

  const [newProjectName, setNewProjectName] = useState('');
  const [newLayerName, setNewLayerName] = useState('');
  const [createFile, setCreateFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [addFile, setAddFile] = useState(null);
  const [addLayerName, setAddLayerName] = useState('');
  const [addFolderId, setAddFolderId] = useState('');
  /** Per-layer draft folder assignment (saved with "Set folder"); synced when `detail` loads */
  const [layerFolderDraft, setLayerFolderDraft] = useState({});

  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [assignIds, setAssignIds] = useState([]);
  const [moveSelect, setMoveSelect] = useState({});
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [folderRenameDraft, setFolderRenameDraft] = useState('');
  const [renamingLayerId, setRenamingLayerId] = useState(null);
  const [layerRenameDraft, setLayerRenameDraft] = useState('');
  const [projectRenameDraft, setProjectRenameDraft] = useState('');

  const isAdmin = user?.role === 'admin';

  const loadDetail = useCallback(async (pid) => {
    if (!pid) {
      setDetail(null);
      return;
    }
    try {
      const d = await getProject(pid);
      setDetail(d);
      const nextFolders = {};
      (d.kml_layers || []).forEach((ly) => {
        nextFolders[ly.id] =
          ly.folder_id != null && ly.folder_id !== undefined ? String(ly.folder_id) : '';
      });
      setLayerFolderDraft(nextFolders);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load project');
      setDetail(null);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (projects?.length && !selectedId) {
      setSelectedId(String(projects[0].id));
    }
  }, [isOpen, projects, selectedId]);

  useEffect(() => {
    if (!isOpen || !selectedId) return;
    loadDetail(parseInt(selectedId, 10));
  }, [isOpen, selectedId, loadDetail]);

  useEffect(() => {
    setRenamingFolderId(null);
    setRenamingLayerId(null);
    setFolderRenameDraft('');
    setLayerRenameDraft('');
    setProjectRenameDraft('');
  }, [selectedId]);

  useEffect(() => {
    if (!isOpen) {
      setRenamingFolderId(null);
      setRenamingLayerId(null);
      setFolderRenameDraft('');
      setLayerRenameDraft('');
      setProjectRenameDraft('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (detail?.name != null) {
      setProjectRenameDraft(detail.name);
    }
  }, [detail?.id, detail?.name]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createFile || !newProjectName.trim()) {
      toast.error('Project name and KML file are required.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await uploadKML(createFile, newProjectName.trim(), newLayerName.trim() || undefined);
      toast.success(r.message || 'Project created.');
      setNewProjectName('');
      setNewLayerName('');
      setCreateFile(null);
      await onRefresh();
      if (r.projectId && onProjectSelect) {
        onProjectSelect(r.projectId);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Create failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRenameProject = async () => {
    const name = projectRenameDraft.trim();
    if (!selectedId || !name) {
      toast.error('Enter a project name.');
      return;
    }
    const pid = parseInt(selectedId, 10);
    if (detail && name === (detail.name || '').trim()) {
      toast.info('Name unchanged.');
      return;
    }
    setSubmitting(true);
    try {
      await updateProjectName(pid, name);
      toast.success('Project renamed.');
      await loadDetail(pid);
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Rename failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddKml = async (e) => {
    e.preventDefault();
    if (!selectedId || !addFile) {
      toast.error('Select a project and KML file.');
      return;
    }
    setSubmitting(true);
    try {
      const folderIdParsed =
        addFolderId.trim() !== '' ? parseInt(addFolderId, 10) : null;
      const r = await addKmlToProject(
        parseInt(selectedId, 10),
        addFile,
        addLayerName.trim() || undefined,
        Number.isFinite(folderIdParsed) ? folderIdParsed : null
      );
      toast.success(r.message || 'KML imported.');
      setAddFile(null);
      setAddLayerName('');
      setAddFolderId('');
      await loadDetail(parseInt(selectedId, 10));
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLayer = async (layerId, layerName) => {
    if (!window.confirm(`Delete layer "${layerName}" and all its blocks? This cannot be undone.`)) return;
    try {
      await deleteKmlLayer(parseInt(selectedId, 10), layerId);
      toast.success('Layer deleted.');
      await loadDetail(parseInt(selectedId, 10));
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed.');
    }
  };

  const handleMoveLayer = async (layerId) => {
    const tgt = parseInt(moveSelect[layerId], 10);
    if (!tgt || tgt === parseInt(selectedId, 10)) {
      toast.error('Choose a different destination project.');
      return;
    }
    try {
      await updateKmlLayer(parseInt(selectedId, 10), layerId, { projectId: tgt });
      toast.success('Layer moved.');
      setMoveSelect((s) => ({ ...s, [layerId]: '' }));
      await loadDetail(parseInt(selectedId, 10));
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Move failed.');
    }
  };

  const handleSetLayerFolder = async (layerId) => {
    const pid = parseInt(selectedId, 10);
    const raw = layerFolderDraft[layerId];
    let payload;
    if (raw === '' || raw === undefined) {
      payload = { folderId: null };
    } else {
      const fid = parseInt(raw, 10);
      if (!Number.isFinite(fid)) {
        toast.error('Invalid folder.');
        return;
      }
      payload = { folderId: fid };
    }
    try {
      await updateKmlLayer(pid, layerId, payload);
      toast.success('Folder updated.');
      await loadDetail(pid);
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update folder.');
    }
  };

  const folderNameById = (fid) => {
    if (fid == null) return null;
    const f = (detail?.folders || []).find((x) => x.id === fid);
    return f ? f.name : `#${fid}`;
  };

  const handleDeleteFolder = async (folderId, name) => {
    if (!window.confirm(`Delete folder "${name}"? Child folders may be deleted; layers lose folder.`)) return;
    try {
      await deleteProjectFolder(parseInt(selectedId, 10), folderId);
      toast.success('Folder deleted.');
      await loadDetail(parseInt(selectedId, 10));
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed.');
    }
  };

  const handleNewFolder = async () => {
    const name = window.prompt('New folder name:');
    if (!name || !selectedId) return;
    try {
      await createProjectFolder(parseInt(selectedId, 10), { name: name.trim() });
      toast.success('Folder created.');
      await loadDetail(parseInt(selectedId, 10));
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed.');
    }
  };

  const beginRenameFolder = (f) => {
    setRenamingFolderId(f.id);
    setFolderRenameDraft(f.name || '');
  };

  const cancelRenameFolder = () => {
    setRenamingFolderId(null);
    setFolderRenameDraft('');
  };

  const saveRenameFolder = async () => {
    const name = folderRenameDraft.trim();
    if (!name || renamingFolderId == null || !selectedId) {
      toast.error('Enter a folder name.');
      return;
    }
    const pid = parseInt(selectedId, 10);
    setSubmitting(true);
    try {
      await updateProjectFolder(pid, renamingFolderId, { name });
      toast.success('Folder renamed.');
      cancelRenameFolder();
      await loadDetail(pid);
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Rename failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const beginRenameLayer = (ly) => {
    setRenamingLayerId(ly.id);
    setLayerRenameDraft(ly.name || '');
  };

  const cancelRenameLayer = () => {
    setRenamingLayerId(null);
    setLayerRenameDraft('');
  };

  const saveRenameLayer = async () => {
    const name = layerRenameDraft.trim();
    if (!name || renamingLayerId == null || !selectedId) {
      toast.error('Enter a layer name.');
      return;
    }
    const pid = parseInt(selectedId, 10);
    setSubmitting(true);
    try {
      await updateKmlLayer(pid, renamingLayerId, { name });
      toast.success('Layer renamed.');
      cancelRenameLayer();
      await loadDetail(pid);
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Rename failed.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isOpen || activeTab !== TABS.members || !selectedId) return;
    (async () => {
      try {
        const pid = parseInt(selectedId, 10);
        const [usersRaw, asn] = await Promise.all([
          getAssignmentEligibleUsers(pid),
          getProjectAssignments(pid)
        ]);
        const usersArr = Array.isArray(usersRaw) ? usersRaw : [];
        setEligibleUsers(usersArr);
        setAssignIds((asn?.userIds || []).map(Number));
      } catch {
        toast.error('Failed to load assignments.');
      }
    })();
  }, [isOpen, activeTab, selectedId]);

  const saveAssignments = async () => {
    if (!selectedId) return;
    try {
      await putProjectAssignments(parseInt(selectedId, 10), assignIds);
      toast.success('Assignments saved.');
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed.');
    }
  };

  const toggleAssign = (uid) => {
    setAssignIds((prev) =>
      prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]
    );
  };

  const handleDeleteProject = async () => {
    if (!selectedId || !detail) return;
    if (
      !window.confirm(
        `Permanently delete project "${detail.name}"?`
      )
    ) {
      return;
    }
    try {
      await deleteProjectAPI(parseInt(selectedId, 10));
      toast.success('Project deleted.');
      setSelectedId('');
      setDetail(null);
      await onRefresh();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="manage-modal-overlay" onClick={onClose}>
      <div
        className="manage-modal-inner"
        onClick={(ev) => ev.stopPropagation()}
        role="dialog"
        aria-labelledby="manage-projects-title"
      >
        <div className="manage-modal-header">
          <h2 id="manage-projects-title">Manage projects</h2>
          <button type="button" className="manage-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="manage-modal-toolbar">
          <label>
            Project
            <select
              value={selectedId}
              onChange={(e) => {
                setSelectedId(e.target.value);
                setActiveTab(TABS.layers);
              }}
            >
              <option value="">—</option>
              {projects.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <nav className="manage-tabs">
            <button
              type="button"
              className={activeTab === TABS.create ? 'active' : ''}
              onClick={() => setActiveTab(TABS.create)}
            >
              <FiFolderPlus /> New project
            </button>
            <button
              type="button"
              className={activeTab === TABS.layers ? 'active' : ''}
              onClick={() => setActiveTab(TABS.layers)}
              disabled={!selectedId}
            >
              <FiLayers /> Layers & folders
            </button>
            <button
              type="button"
              className={activeTab === TABS.members ? 'active' : ''}
              onClick={() => setActiveTab(TABS.members)}
              disabled={!selectedId}
            >
              <FiUsers /> Team access
            </button>
            {isAdmin && (
              <button
                type="button"
                className={activeTab === TABS.danger ? 'active danger' : 'danger'}
                onClick={() => setActiveTab(TABS.danger)}
                disabled={!selectedId}
              >
                <FiAlertTriangle /> Delete project
              </button>
            )}
          </nav>
        </div>

        <div className="manage-modal-body">
          {activeTab === TABS.create && (
            <form className="manage-section" onSubmit={handleCreate}>
              <p className="manage-lead">
                Create a new project from one KML. You can import more KMLs later under Layers.
              </p>
              <div className="manage-field">
                <label htmlFor="mp-proj-name">Project name</label>
                <input
                  id="mp-proj-name"
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="e.g. Survey North"
                  required
                />
              </div>
              <div className="manage-field">
                <label htmlFor="mp-layer-name">First layer name (optional)</label>
                <input
                  id="mp-layer-name"
                  type="text"
                  value={newLayerName}
                  onChange={(e) => setNewLayerName(e.target.value)}
                  placeholder="Defaults to project name"
                />
              </div>
              <div className="manage-field">
                <label htmlFor="mp-kml">KML file</label>
                <input
                  id="mp-kml"
                  type="file"
                  accept=".kml,.KML"
                  onChange={(e) => setCreateFile(e.target.files?.[0] || null)}
                />
              </div>
              <button type="submit" className="manage-primary" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create project'}
              </button>
            </form>
          )}

          {activeTab === TABS.layers && selectedId && detail && (
            <div className="manage-section layers-section">
              <div className="manage-project-rename-panel manage-field">
                <label htmlFor="mp-project-rename">Project name</label>
                <div className="manage-inline-actions">
                  <input
                    id="mp-project-rename"
                    type="text"
                    value={projectRenameDraft}
                    onChange={(e) => setProjectRenameDraft(e.target.value)}
                    placeholder="Project display name"
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    className="manage-secondary"
                    onClick={() => setProjectRenameDraft(detail?.name || '')}
                    disabled={submitting}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="manage-primary"
                    onClick={handleRenameProject}
                    disabled={
                      submitting ||
                      !projectRenameDraft.trim() ||
                      projectRenameDraft.trim() === (detail?.name || '').trim()
                    }
                  >
                    Save name
                  </button>
                </div>
              </div>
              <div className="manage-subpanel">
                <h3>Add KML to this project</h3>
                <form className="manage-inline-form" onSubmit={handleAddKml}>
                  <input
                    type="text"
                    placeholder="Layer display name"
                    value={addLayerName}
                    onChange={(e) => setAddLayerName(e.target.value)}
                  />
                  <label className="manage-inline-label">
                    Group (folder)
                    <select
                      value={addFolderId}
                      onChange={(e) => setAddFolderId(e.target.value)}
                      title="Place this KML under a folder for filtering and reports"
                    >
                      <option value="">— None (ungrouped) —</option>
                      {(detail.folders || []).map((f) => (
                        <option key={f.id} value={String(f.id)}>
                          {f.parent_id ? '↳ ' : ''}
                          {f.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <input type="file" accept=".kml,.KML" onChange={(e) => setAddFile(e.target.files?.[0])} />
                  <button type="submit" className="manage-primary" disabled={submitting}>
                    Import layer
                  </button>
                </form>
              </div>

              <div className="manage-subpanel">
                <div className="manage-subhead">
                  <h3>Folders</h3>
                  <button type="button" className="manage-secondary" onClick={handleNewFolder}>
                    New folder
                  </button>
                </div>
                <ul className="manage-folder-list">
                  {(detail.folders || []).map((f) => (
                    <li key={f.id} className="manage-folder-row">
                      {renamingFolderId === f.id ? (
                        <div className="manage-rename-inline">
                          <span className="manage-folder-prefix">{f.parent_id ? '↳ ' : ''}</span>
                          <input
                            type="text"
                            value={folderRenameDraft}
                            onChange={(e) => setFolderRenameDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveRenameFolder();
                              if (e.key === 'Escape') cancelRenameFolder();
                            }}
                            autoFocus
                            className="manage-rename-input"
                            aria-label="Folder name"
                          />
                          <button
                            type="button"
                            className="manage-secondary"
                            disabled={submitting}
                            onClick={saveRenameFolder}
                          >
                            Save
                          </button>
                          <button type="button" className="manage-link-cancel" onClick={cancelRenameFolder}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          <span className="manage-folder-label">
                            {f.parent_id ? '↳ ' : ''}
                            {f.name}
                          </span>
                          <span className="manage-folder-actions">
                            <button
                              type="button"
                              className="manage-link"
                              onClick={() => beginRenameFolder(f)}
                              title="Rename folder"
                            >
                              Rename
                            </button>
                            <button
                              type="button"
                              className="manage-link-danger"
                              onClick={() => handleDeleteFolder(f.id, f.name)}
                            >
                              Remove
                            </button>
                          </span>
                        </>
                      )}
                    </li>
                  ))}
                  {!(detail.folders || []).length && <li className="muted">No folders yet.</li>}
                </ul>
              </div>

              <div className="manage-subpanel">
                <h3>KML layers</h3>
                <table className="manage-layer-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Folder</th>
                      <th>Blocks</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.kml_layers || []).map((ly) => {
                      const nc = (detail.blocks || []).filter((b) => b.kml_layer_id === ly.id).length;
                      return (
                        <tr key={ly.id}>
                          <td>
                            {renamingLayerId === ly.id ? (
                              <div className="manage-rename-inline manage-rename-td">
                                <input
                                  type="text"
                                  value={layerRenameDraft}
                                  onChange={(e) => setLayerRenameDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveRenameLayer();
                                    if (e.key === 'Escape') cancelRenameLayer();
                                  }}
                                  autoFocus
                                  className="manage-rename-input"
                                  aria-label="Layer display name"
                                />
                                <button
                                  type="button"
                                  className="manage-secondary"
                                  disabled={submitting}
                                  onClick={saveRenameLayer}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="manage-link-cancel"
                                  onClick={cancelRenameLayer}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <>
                                <span className="manage-layer-title">{ly.name}</span>{' '}
                                <button
                                  type="button"
                                  className="manage-link"
                                  onClick={() => beginRenameLayer(ly)}
                                  title="Rename layer"
                                >
                                  Rename
                                </button>
                              </>
                            )}
                          </td>
                          <td>
                            <div className="manage-layer-folder-cell">
                              <span className="muted" title="Current folder">
                                {folderNameById(ly.folder_id) || '—'}
                              </span>
                            </div>
                          </td>
                          <td>{nc}</td>
                          <td>
                            <div className="manage-layer-actions">
                              <select
                                value={layerFolderDraft[ly.id] ?? ''}
                                onChange={(e) =>
                                  setLayerFolderDraft((prev) => ({
                                    ...prev,
                                    [ly.id]: e.target.value
                                  }))
                                }
                                aria-label="Assign to folder"
                                title="Group this layer with others in the same folder (map filter & reports)"
                              >
                                <option value="">— None —</option>
                                {(detail.folders || []).map((f) => (
                                  <option key={f.id} value={String(f.id)}>
                                    {f.parent_id ? '↳ ' : ''}
                                    {f.name}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="manage-secondary"
                                onClick={() => handleSetLayerFolder(ly.id)}
                              >
                                Set folder
                              </button>
                              <select
                                value={moveSelect[ly.id] || ''}
                                onChange={(e) =>
                                  setMoveSelect((prev) => ({ ...prev, [ly.id]: e.target.value }))
                                }
                                aria-label="Move to project"
                              >
                                <option value="">Move to project…</option>
                                {projects
                                  .filter((p) => p.id !== parseInt(selectedId, 10))
                                  .map((p) => (
                                    <option key={p.id} value={String(p.id)}>
                                      {p.name}
                                    </option>
                                  ))}
                              </select>
                              <button type="button" className="manage-secondary" onClick={() => handleMoveLayer(ly.id)}>
                                Move
                              </button>
                              <button
                                type="button"
                                className="manage-link-danger"
                                onClick={() => handleDeleteLayer(ly.id, ly.name)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === TABS.members && selectedId && (
            <div className="manage-section">
              <p className="manage-lead">
                Choose which editors, viewers, and supervisors can access this project. Administrators always have full access.
              </p>
              <div className="manage-checklist">
                {eligibleUsers.map((u) => (
                  <label key={u.id} className="manage-check-item">
                    <input
                      type="checkbox"
                      checked={assignIds.includes(u.id)}
                      onChange={() => toggleAssign(u.id)}
                    />
                    <span>
                      {u.username}{' '}
                      <small className="muted">({u.role})</small>
                    </span>
                  </label>
                ))}
              </div>
              <button type="button" className="manage-primary" onClick={saveAssignments}>
                Save assignments
              </button>
            </div>
          )}

          {activeTab === TABS.danger && selectedId && detail && (
            <div className="manage-section danger-zone">
              <p>
                Delete <strong>{detail.name}</strong> and all of its folders, layers, blocks, and status data.
              </p>
              <button type="button" className="manage-danger-btn" onClick={handleDeleteProject}>
                Delete project permanently
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManageProjectsModal;
